import {produce} from 'immer';
import {
  FORM_SETTINGS_FETCH_REQUESTED,
  FORM_SETTINGS_FETCH_SUCCEEDED,
  FORM_SETTINGS_FETCH_FAILED,
  FORM_SETTINGS_UPDATE_REQUESTED,
  FORM_SETTINGS_UPDATE_SUCCEEDED,
  FORM_SETTINGS_UPDATE_FAILED,
  FORM_SETTINGS_UI_STATE_UPDATED,
} from '@packages/systems/designer/actions';
import type {
  FormSettingsPayload,
  FormSettingsError,
} from '@packages/systems/designer/forms/types';
import type {FormSettingsAction} from './actions';

/** Network status enum for clear state management */
export const NETWORK_STATUSES = {
  INIT: 'INIT',
  LOADING: 'LOADING',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
} as const;

export type LoadingState =
  (typeof NETWORK_STATUSES)[keyof typeof NETWORK_STATUSES];

/**
 * Form settings store state using modern Webflow patterns
 * Uses plain objects with Immer for immutable updates
 */
interface FormSettingsState {
  /** Entity storage keyed by elementId or elementId:componentId */
  entities: Record<string, FormSettingsPayload>;

  /** Per-entity fetching states for granular UI control */
  fetchingStates: Record<string, LoadingState>;

  /** Per-entity updating states for granular UI control */
  updatingStates: Record<string, LoadingState>;

  /** Per-entity error states for better error handling */
  errors: Record<string, FormSettingsError | null>;

  /** Per-entity UI configuration states for sync across instances */
  uiStates: Record<
    string,
    {
      isWebflowConfigured: boolean;
      isEmailConfigured: boolean;
    }
  >;
}

/** Initial state following modern Webflow patterns */
export const initialFormSettingsState: FormSettingsState = {
  entities: {},
  fetchingStates: {},
  updatingStates: {},
  errors: {},
  uiStates: {},
};

/** Helper to create entity key for consistent keying strategy */
const createEntityKey = (elementId: string, componentId?: string): string =>
  // componentId is set to 'default' on the backend, we'll still include it in the store,
  // but only want to add it part of the entity key if it's not the default value
  componentId && componentId !== 'default'
    ? `${elementId}:${componentId}`
    : elementId;

/**
 * Create default form settings matching FormSettingsPayload structure
 * Factory function for consistent default state management
 */
const createDefaultFormSettings = ({
  elementId,
  componentId,
}: {
  elementId: string;
  componentId: string;
}): FormSettingsPayload => ({
  id: undefined,
  elementId,
  componentId,
  localeId: undefined,
  emailSenderName: undefined,
  emailSender: undefined,
  emailSubject: undefined,
  emailReplyTos: [],
  emailDestinations: undefined,
  emailFormTemplate: undefined,
  sendEmailConfirmation: undefined,
  redirectURL: undefined,
  redirectAction: undefined,
  redirectMethod: undefined,
  showFormSubmissionCount: undefined,
  captchaConfiguration: undefined,
  restrictUploadedFileAccess: undefined,
  registeredWebhooks: [],
});

/**
 * Form Settings Store using Immer for immutable updates
 */
export const formSettingsReducer = (
  state = initialFormSettingsState,
  action: FormSettingsAction
): FormSettingsState => {
  switch (action.type) {
    case FORM_SETTINGS_FETCH_REQUESTED: {
      return produce(state, (draft) => {
        const {elementId, componentId} = action.payload;
        const key = createEntityKey(elementId, componentId);

        // Set fetching state for this specific entity
        draft.fetchingStates[key] = NETWORK_STATUSES.LOADING;
        // Clear any previous errors for this entity
        draft.errors[key] = null;
      });
    }

    case FORM_SETTINGS_FETCH_SUCCEEDED: {
      return produce(state, (draft) => {
        // Convert Immutable Map meta to plain object for use with immer
        const meta = action.meta.toJS();
        const elementId = meta.elementId;
        const componentId = meta.componentId;
        const key = createEntityKey(elementId, componentId);

        // Store the successfully fetched settings
        draft.entities[key] =
          action.payload.response &&
          Object.keys(action.payload.response).length > 0
            ? action.payload.response
            : createDefaultFormSettings({elementId, componentId});

        // Update fetching state for this specific entity
        draft.fetchingStates[key] = NETWORK_STATUSES.SUCCEEDED;

        // Clear any previous errors
        draft.errors[key] = null;
      });
    }

    case FORM_SETTINGS_FETCH_FAILED: {
      return produce(state, (draft) => {
        // Convert Immutable Map meta to plain object for use with immer
        const meta = action.meta.toJS();
        const elementId = meta.elementId;
        const componentId = meta.componentId;
        const key = createEntityKey(elementId, componentId);

        // Set failed fetching state
        draft.fetchingStates[key] = NETWORK_STATUSES.FAILED;

        // Store the error for this specific entity
        draft.errors[key] = {
          type: 'FETCH_ERROR',
          message: 'Failed to fetch form settings',
        };
      });
    }

    case FORM_SETTINGS_UPDATE_REQUESTED: {
      return produce(state, (draft) => {
        const {elementId, componentId} = action.payload;
        const key = createEntityKey(elementId, componentId);

        // Set updating state for this specific entity
        draft.updatingStates[key] = NETWORK_STATUSES.LOADING;

        // Clear any previous errors
        draft.errors[key] = null;
      });
    }

    case FORM_SETTINGS_UPDATE_SUCCEEDED: {
      return produce(state, (draft) => {
        // Convert Immutable Map meta to plain object for use with immer
        const meta = action.meta.toJS();
        const elementId = meta.elementId;
        const componentId = meta.componentId;
        const key = createEntityKey(elementId, componentId);

        // Update the entity with the server response
        draft.entities[key] = action.payload.response;

        // Set success updating state
        draft.updatingStates[key] = NETWORK_STATUSES.SUCCEEDED;

        // Clear any previous errors
        draft.errors[key] = null;
      });
    }

    case FORM_SETTINGS_UPDATE_FAILED: {
      return produce(state, (draft) => {
        // Convert Immutable Map meta to plain object for use with immer
        const meta = action.meta.toJS();
        const elementId = meta.elementId;
        const componentId = meta.componentId;
        const key = createEntityKey(elementId, componentId);

        // Set failed updating state
        draft.updatingStates[key] = NETWORK_STATUSES.FAILED;

        // Store the error for this specific entity
        draft.errors[key] = {
          type: 'UPDATE_ERROR',
          message: 'Failed to update form settings',
        };
      });
    }

    case FORM_SETTINGS_UI_STATE_UPDATED: {
      return produce(state, (draft) => {
        const {
          elementId,
          componentId,
          uiState: {isWebflowConfigured, isEmailConfigured},
        } = action.payload;
        const key = createEntityKey(elementId, componentId);

        // Initialize UI state if it doesn't exist
        if (!draft.uiStates[key]) {
          draft.uiStates[key] = {
            isWebflowConfigured: false,
            isEmailConfigured: false,
          };
        }

        // Update only the provided fields
        if (isWebflowConfigured !== undefined) {
          draft.uiStates[key].isWebflowConfigured = isWebflowConfigured;
        }
        if (isEmailConfigured !== undefined) {
          draft.uiStates[key].isEmailConfigured = isEmailConfigured;
        }
      });
    }
  }
  return state;
};

export type {FormSettingsState};
